'use client';

/**
 * Tool Pathways - Tech Tree Visualization
 * Shows 3 pathways for using tools across business phases
 */

import { useState } from 'react';
import Link from 'next/link';
import ToolSEO from '@/components/ToolSEO';

// Business phases
type BusinessPhase = 'inception' | 'growth' | 'maturity';

// Pathway types
type PathwayType = 'product' | 'engineering' | 'compliance';

interface ToolNode {
  id: string;
  name: string;
  description: string;
  path: string;
  phase: BusinessPhase;
  pathway: PathwayType;
  icon: string;
  isPremium?: boolean;
  dependencies?: string[]; // IDs of tools that should come before
}

// All tools organized by pathway and phase
const TOOLS: ToolNode[] = [
  // === PRODUCT & BUSINESS PATHWAY ===
  // Inception
  {
    id: 'foundry',
    name: 'Foundry',
    description: 'Lean Canvas & business planning',
    path: '/tools/foundry',
    phase: 'inception',
    pathway: 'product',
    icon: '🏗️',
    isPremium: true,
  },
  {
    id: 'build-vs-buy',
    name: 'Build vs Buy Matrix',
    description: 'Make or buy decisions',
    path: '/tools/build-vs-buy-matrix',
    phase: 'inception',
    pathway: 'product',
    icon: '⚖️',
    dependencies: ['foundry'],
  },
  {
    id: 'tech-stack-decision',
    name: 'Tech Stack Decision',
    description: 'Choose your technology stack',
    path: '/tools/tech-stack-decision',
    phase: 'inception',
    pathway: 'product',
    icon: '🔧',
    isPremium: true,
    dependencies: ['build-vs-buy'],
  },
  // Growth
  {
    id: 'wardley-map',
    name: 'Wardley Map Builder',
    description: 'Strategic positioning',
    path: '/tools/wardley-map-builder',
    phase: 'growth',
    pathway: 'product',
    icon: '🗺️',
    dependencies: ['foundry'],
  },
  {
    id: 'vendor-risk',
    name: 'Vendor Risk Assessment',
    description: 'Evaluate vendor dependencies',
    path: '/tools/vendor-risk-assessment',
    phase: 'growth',
    pathway: 'product',
    icon: '🔍',
    dependencies: ['tech-stack-decision'],
  },
  {
    id: 'tech-debt',
    name: 'Tech Debt Prioritizer',
    description: 'Manage technical debt',
    path: '/tools/tech-debt-prioritizer',
    phase: 'growth',
    pathway: 'product',
    icon: '📊',
    dependencies: ['tech-stack-decision'],
  },
  // Maturity
  {
    id: 'board-presentation',
    name: 'Board Presentation',
    description: 'Executive presentations',
    path: '/tools/board-presentation',
    phase: 'maturity',
    pathway: 'product',
    icon: '📈',
    isPremium: true,
    dependencies: ['wardley-map'],
  },
  {
    id: 'cto-rating',
    name: 'CTO Rating Framework',
    description: 'Self-assessment for CTOs',
    path: '/tools/cto-rating-framework',
    phase: 'maturity',
    pathway: 'product',
    icon: '⭐',
    dependencies: ['board-presentation'],
  },

  // === ENGINEERING & TECHNOLOGY PATHWAY ===
  // Inception
  {
    id: 'tech-radar',
    name: 'Tech Radar',
    description: 'Technology adoption tracking',
    path: '/tools/tech-radar',
    phase: 'inception',
    pathway: 'engineering',
    icon: '📡',
    isPremium: true,
  },
  {
    id: 'team-scaling',
    name: 'Team Scaling Calculator',
    description: 'Plan team growth',
    path: '/tools/team-scaling-calculator',
    phase: 'inception',
    pathway: 'engineering',
    icon: '👥',
    dependencies: ['tech-radar'],
  },
  {
    id: 'cloud-cost',
    name: 'Cloud Cost Estimator',
    description: 'Estimate infrastructure costs',
    path: '/tools/cloud-cost-estimator',
    phase: 'inception',
    pathway: 'engineering',
    icon: '☁️',
    dependencies: ['tech-radar'],
  },
  // Growth
  {
    id: 'system-design',
    name: 'System Design Canvas',
    description: 'Architecture diagramming',
    path: '/tools/system-design',
    phase: 'growth',
    pathway: 'engineering',
    icon: '🏛️',
    isPremium: true,
    dependencies: ['tech-radar', 'cloud-cost'],
  },
  {
    id: 'engineering-ladder',
    name: 'Engineering Ladder',
    description: 'Career framework design',
    path: '/tools/engineering-ladder',
    phase: 'growth',
    pathway: 'engineering',
    icon: '🪜',
    isPremium: true,
    dependencies: ['team-scaling'],
  },
  {
    id: 'team-topology',
    name: 'Team Topology Designer',
    description: 'Team structure planning',
    path: '/tools/team-topology',
    phase: 'growth',
    pathway: 'engineering',
    icon: '🔀',
    isPremium: true,
    dependencies: ['team-scaling'],
  },
  {
    id: 'one-on-one',
    name: '1:1 Meeting Assistant',
    description: 'Effective one-on-ones',
    path: '/tools/one-on-one',
    phase: 'growth',
    pathway: 'engineering',
    icon: '💬',
    isPremium: true,
    dependencies: ['engineering-ladder'],
  },
  // Maturity
  {
    id: 'api-contract',
    name: 'API Contract Designer',
    description: 'API specification builder',
    path: '/tools/api-contract',
    phase: 'maturity',
    pathway: 'engineering',
    icon: '📜',
    isPremium: true,
    dependencies: ['system-design'],
  },
  {
    id: 'engineering-org',
    name: 'Engineering Org Design',
    description: 'Organization structure',
    path: '/tools/engineering-org-design',
    phase: 'maturity',
    pathway: 'engineering',
    icon: '🏢',
    dependencies: ['team-topology', 'engineering-ladder'],
  },
  {
    id: 'arch-maturity',
    name: 'Architecture Maturity',
    description: 'Assess architecture maturity',
    path: '/tools/architecture-maturity-assessment',
    phase: 'maturity',
    pathway: 'engineering',
    icon: '📐',
    dependencies: ['system-design'],
  },

  // === COMPLIANCE & GOVERNANCE PATHWAY ===
  // Inception
  {
    id: 'data-classification',
    name: 'Data Classification',
    description: 'Classify data sensitivity',
    path: '/tools/data-classification',
    phase: 'inception',
    pathway: 'compliance',
    icon: '🗂️',
    isPremium: true,
  },
  {
    id: 'security-checklist',
    name: 'Security Checklist',
    description: 'Security assessment basics',
    path: '/tools/security-assessment-checklist',
    phase: 'inception',
    pathway: 'compliance',
    icon: '✅',
    dependencies: ['data-classification'],
  },
  // Growth
  {
    id: 'gdpr',
    name: 'GDPR Compliance',
    description: 'GDPR readiness check',
    path: '/tools/gdpr-compliance',
    phase: 'growth',
    pathway: 'compliance',
    icon: '🇪🇺',
    dependencies: ['data-classification'],
  },
  {
    id: 'pia-dpia',
    name: 'PIA/DPIA Generator',
    description: 'Privacy impact assessments',
    path: '/tools/pia-dpia',
    phase: 'growth',
    pathway: 'compliance',
    icon: '🔐',
    isPremium: true,
    dependencies: ['gdpr', 'data-classification'],
  },
  {
    id: 'incident-response',
    name: 'Incident Response',
    description: 'Incident management plans',
    path: '/tools/incident-response',
    phase: 'growth',
    pathway: 'compliance',
    icon: '🚨',
    dependencies: ['security-checklist'],
  },
  {
    id: 'business-continuity',
    name: 'Business Continuity',
    description: 'BC/DR planning',
    path: '/tools/business-continuity',
    phase: 'growth',
    pathway: 'compliance',
    icon: '🔄',
    dependencies: ['incident-response'],
  },
  // Maturity
  {
    id: 'soc2',
    name: 'SOC 2 Readiness',
    description: 'SOC 2 compliance check',
    path: '/tools/soc2-readiness',
    phase: 'maturity',
    pathway: 'compliance',
    icon: '🏅',
    dependencies: ['security-checklist', 'incident-response'],
  },
  {
    id: 'iso27001',
    name: 'ISO 27001 Gap Analysis',
    description: 'ISO 27001 readiness',
    path: '/tools/iso27001-gap-analysis',
    phase: 'maturity',
    pathway: 'compliance',
    icon: '📋',
    dependencies: ['soc2'],
  },
  {
    id: 'dora',
    name: 'DORA Compliance',
    description: 'Digital operational resilience',
    path: '/tools/dora-compliance',
    phase: 'maturity',
    pathway: 'compliance',
    icon: '🏛️',
    dependencies: ['business-continuity', 'iso27001'],
  },
];

const PHASE_CONFIG: Record<BusinessPhase, { label: string; description: string; color: string }> = {
  inception: {
    label: 'Inception',
    description: 'Starting your venture',
    color: '#22c55e',
  },
  growth: {
    label: 'Growth',
    description: 'Scaling operations',
    color: '#3b82f6',
  },
  maturity: {
    label: 'Maturity',
    description: 'Enterprise excellence',
    color: '#a855f7',
  },
};

const PATHWAY_CONFIG: Record<PathwayType, { label: string; description: string; icon: string; color: string }> = {
  product: {
    label: 'Product & Business',
    description: 'Strategy, planning, and business decisions',
    icon: '🎯',
    color: '#E89841',
  },
  engineering: {
    label: 'Engineering & Technology',
    description: 'Architecture, teams, and technical excellence',
    icon: '⚙️',
    color: '#3b82f6',
  },
  compliance: {
    label: 'Compliance & Governance',
    description: 'Security, privacy, and regulatory compliance',
    icon: '🛡️',
    color: '#22c55e',
  },
};

export default function ToolPathwaysPage() {
  const [selectedPathway, setSelectedPathway] = useState<PathwayType | 'all'>('all');
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);

  const phases: BusinessPhase[] = ['inception', 'growth', 'maturity'];
  const pathways: PathwayType[] = ['product', 'engineering', 'compliance'];

  const filteredTools = selectedPathway === 'all'
    ? TOOLS
    : TOOLS.filter(t => t.pathway === selectedPathway);

  const getToolsForCell = (phase: BusinessPhase, pathway: PathwayType) => {
    return filteredTools.filter(t => t.phase === phase && t.pathway === pathway);
  };

  // Calculate SVG paths for dependencies
  const getDependencyLines = () => {
    if (selectedPathway === 'all') return []; // Too complex to show all

    const lines: Array<{ from: ToolNode; to: ToolNode }> = [];
    filteredTools.forEach(tool => {
      if (tool.dependencies) {
        tool.dependencies.forEach(depId => {
          const dep = TOOLS.find(t => t.id === depId);
          if (dep && dep.pathway === selectedPathway) {
            lines.push({ from: dep, to: tool });
          }
        });
      }
    });
    return lines;
  };

  return (
    <>
      <ToolSEO
        toolName="Tool Pathways"
        toolSlug="pathways"
        description="Discover which CTO tools to use at each business phase. Visual tech tree for Product, Engineering, and Compliance pathways."
        category="planner"
      />
      <main className="min-h-screen bg-background text-foreground py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Tool Pathways</h1>
            <p className="text-foreground-secondary text-lg max-w-2xl mx-auto">
              Navigate your journey from startup to enterprise with the right tools at each phase.
              Three pathways guide you through Product, Engineering, and Compliance.
            </p>
          </div>

          {/* Pathway filter */}
          <div className="flex justify-center gap-4 mb-12 flex-wrap">
            <button
              onClick={() => setSelectedPathway('all')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                selectedPathway === 'all'
                  ? 'bg-accent text-accent-foreground'
                  : 'bg-cream/10 text-foreground hover:bg-cream/20'
              }`}
            >
              All Pathways
            </button>
            {pathways.map(pathway => {
              const config = PATHWAY_CONFIG[pathway];
              return (
                <button
                  key={pathway}
                  onClick={() => setSelectedPathway(pathway)}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    selectedPathway === pathway
                      ? 'text-accent-foreground'
                      : 'bg-cream/10 text-foreground hover:bg-cream/20'
                  }`}
                  style={{
                    backgroundColor: selectedPathway === pathway ? config.color : undefined,
                  }}
                >
                  <span>{config.icon}</span>
                  {config.label}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex justify-center gap-8 mb-8 flex-wrap">
            {phases.map(phase => {
              const config = PHASE_CONFIG[phase];
              return (
                <div key={phase} className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: config.color }} />
                  <span className="text-sm text-foreground-secondary">{config.label}</span>
                </div>
              );
            })}
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 bg-accent-muted text-accent text-xs rounded">PREMIUM</span>
              <span className="text-sm text-foreground-secondary">Premium tool</span>
            </div>
          </div>

          {/* Tech Tree */}
          <div className="relative">
            {/* Phase labels (vertical) */}
            <div className="grid gap-8" style={{
              gridTemplateColumns: selectedPathway === 'all' ? '120px 1fr 1fr 1fr' : '120px 1fr'
            }}>
              {/* Header row */}
              <div /> {/* Empty cell for phase labels column */}
              {selectedPathway === 'all' ? (
                pathways.map(pathway => {
                  const config = PATHWAY_CONFIG[pathway];
                  return (
                    <div key={pathway} className="text-center pb-4 border-b border-border-muted">
                      <div className="text-2xl mb-1">{config.icon}</div>
                      <div className="font-semibold" style={{ color: config.color }}>
                        {config.label}
                      </div>
                      <div className="text-xs text-foreground-tertiary">{config.description}</div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center pb-4 border-b border-border-muted">
                  <div className="text-2xl mb-1">{PATHWAY_CONFIG[selectedPathway].icon}</div>
                  <div className="font-semibold" style={{ color: PATHWAY_CONFIG[selectedPathway].color }}>
                    {PATHWAY_CONFIG[selectedPathway].label}
                  </div>
                  <div className="text-xs text-foreground-tertiary">{PATHWAY_CONFIG[selectedPathway].description}</div>
                </div>
              )}

              {/* Phase rows */}
              {phases.map((phase, phaseIdx) => {
                const phaseConfig = PHASE_CONFIG[phase];
                return (
                  <>
                    {/* Phase label */}
                    <div
                      key={`label-${phase}`}
                      className="flex flex-col justify-center items-center py-8"
                    >
                      <div
                        className="w-3 h-3 rounded-full mb-2"
                        style={{ backgroundColor: phaseConfig.color }}
                      />
                      <div className="font-bold text-lg" style={{ color: phaseConfig.color }}>
                        {phaseConfig.label}
                      </div>
                      <div className="text-xs text-foreground-muted text-center">
                        {phaseConfig.description}
                      </div>
                      {phaseIdx < phases.length - 1 && (
                        <div
                          className="w-0.5 h-16 mt-4"
                          style={{ backgroundColor: `${phaseConfig.color}40` }}
                        />
                      )}
                    </div>

                    {/* Tool cells */}
                    {selectedPathway === 'all' ? (
                      pathways.map(pathway => (
                        <div
                          key={`${phase}-${pathway}`}
                          className="py-4 min-h-[200px]"
                        >
                          <div className="space-y-3">
                            {getToolsForCell(phase, pathway).map(tool => (
                              <ToolCard
                                key={tool.id}
                                tool={tool}
                                isHovered={hoveredTool === tool.id}
                                onHover={setHoveredTool}
                                phaseColor={phaseConfig.color}
                                pathwayColor={PATHWAY_CONFIG[pathway].color}
                              />
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div
                        key={`${phase}-single`}
                        className="py-4 min-h-[200px]"
                      >
                        <div className="flex flex-wrap gap-4 justify-center">
                          {getToolsForCell(phase, selectedPathway).map(tool => (
                            <ToolCard
                              key={tool.id}
                              tool={tool}
                              isHovered={hoveredTool === tool.id}
                              onHover={setHoveredTool}
                              phaseColor={phaseConfig.color}
                              pathwayColor={PATHWAY_CONFIG[selectedPathway].color}
                              large
                            />
                          ))}
                        </div>
                        {/* Connection lines for single pathway view */}
                        {phaseIdx < phases.length - 1 && (
                          <div className="flex justify-center mt-4">
                            <svg width="40" height="40" className="text-foreground-muted">
                              <path
                                d="M20 0 L20 40"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeDasharray="4 4"
                                fill="none"
                              />
                              <path
                                d="M15 30 L20 40 L25 30"
                                stroke="currentColor"
                                strokeWidth="2"
                                fill="none"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })}
            </div>
          </div>

          {/* Journey Summary */}
          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {pathways.map(pathway => {
              const config = PATHWAY_CONFIG[pathway];
              const pathwayTools = TOOLS.filter(t => t.pathway === pathway);
              return (
                <div
                  key={pathway}
                  className="p-6 border border-border-muted rounded-lg hover:border-border transition-colors"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">{config.icon}</span>
                    <div>
                      <h3 className="font-bold text-lg" style={{ color: config.color }}>
                        {config.label}
                      </h3>
                      <p className="text-sm text-foreground-tertiary">{config.description}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {phases.map(phase => {
                      const phaseTools = pathwayTools.filter(t => t.phase === phase);
                      return (
                        <div key={phase} className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: PHASE_CONFIG[phase].color }}
                          />
                          <span className="text-sm text-foreground-secondary">
                            {PHASE_CONFIG[phase].label}:
                          </span>
                          <span className="text-sm text-foreground-tertiary">
                            {phaseTools.length} tools
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setSelectedPathway(pathway)}
                    className="mt-4 text-sm hover:underline"
                    style={{ color: config.color }}
                  >
                    Explore pathway →
                  </button>
                </div>
              );
            })}
          </div>

          {/* Getting Started */}
          <div className="mt-16 text-center p-8 bg-cream/5 border border-border-muted rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Where Should You Start?</h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="p-4">
                <div className="text-3xl mb-2">🚀</div>
                <h3 className="font-semibold mb-2">New Startup</h3>
                <p className="text-sm text-foreground-tertiary mb-3">
                  Begin with Foundry for business planning, Tech Radar for technology decisions, and Data Classification for compliance basics.
                </p>
                <Link href="/tools/foundry" className="text-accent hover:underline text-sm">
                  Start with Foundry →
                </Link>
              </div>
              <div className="p-4">
                <div className="text-3xl mb-2">📈</div>
                <h3 className="font-semibold mb-2">Scaling Company</h3>
                <p className="text-sm text-foreground-tertiary mb-3">
                  Focus on Engineering Ladder, Team Topology, and System Design to build scalable teams and architecture.
                </p>
                <Link href="/tools/engineering-ladder" className="text-accent hover:underline text-sm">
                  Start with Engineering Ladder →
                </Link>
              </div>
              <div className="p-4">
                <div className="text-3xl mb-2">🏢</div>
                <h3 className="font-semibold mb-2">Enterprise</h3>
                <p className="text-sm text-foreground-tertiary mb-3">
                  Prioritize Board Presentations, SOC 2 Readiness, and ISO 27001 for governance and executive communication.
                </p>
                <Link href="/tools/board-presentation" className="text-accent hover:underline text-sm">
                  Start with Board Presentation →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

function ToolCard({
  tool,
  isHovered,
  onHover,
  phaseColor,
  pathwayColor,
  large = false,
}: {
  tool: ToolNode;
  isHovered: boolean;
  onHover: (id: string | null) => void;
  phaseColor: string;
  pathwayColor: string;
  large?: boolean;
}) {
  return (
    <Link
      href={tool.path}
      className={`block p-4 border rounded-lg transition-all ${
        large ? 'w-64' : 'w-full'
      } ${
        isHovered
          ? 'border-accent bg-accent-muted scale-105'
          : 'border-border-muted bg-cream/5 hover:border-border'
      }`}
      onMouseEnter={() => onHover(tool.id)}
      onMouseLeave={() => onHover(null)}
      style={{
        borderLeftWidth: 3,
        borderLeftColor: pathwayColor,
      }}
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl">{tool.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground truncate">{tool.name}</span>
            {tool.isPremium && (
              <span className="px-1.5 py-0.5 bg-accent-muted text-accent text-xs rounded shrink-0">
                PRO
              </span>
            )}
          </div>
          <p className="text-xs text-foreground-tertiary mt-1 line-clamp-2">{tool.description}</p>
        </div>
      </div>
    </Link>
  );
}
